import DOMPurify from "dompurify";
import React, { useState } from "react";
import { generateCampaign } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { Loader2, Sparkles, Wand2 } from "lucide-react";

const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-brand";
const emptyForm = { nom: "", kind: "prospection", region: "", ville: "", secteur: "", scheduled_at: "" };

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-neutral-600 mb-1">{label}</label>
    {children}
  </div>
);

const RecipientPicker = ({ prospects, selected, toggle }) => (
  <div className="max-h-56 overflow-y-auto border border-neutral-200 rounded-lg divide-y divide-neutral-100" data-testid="campaign-recipients">
    {prospects.length === 0 && <p className="text-sm text-neutral-400 p-4">Aucun prospect. Ajoutez-en d'abord.</p>}
    {prospects.map((p) => (
      <label key={p.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 cursor-pointer">
        <input type="checkbox" checked={!!selected[p.id]} onChange={() => toggle(p.id)} className="accent-brand w-4 h-4" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-neutral-800 truncate">{p.societe} {p.ville ? `· ${p.ville}` : ""}</p>
          <p className="text-xs text-neutral-500 truncate">{p.email || "⚠️ pas d'email"}</p>
        </div>
      </label>
    ))}
  </div>
);

const CampaignForm = ({ prospects, onCreate }) => {
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState({});
  const [template, setTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const { toast } = useToast();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);

  const selectByVille = () => {
    const v = form.ville.trim().toLowerCase();
    if (!v) return;
    const next = {};
    prospects.forEach((p) => { if ((p.ville || "").toLowerCase().includes(v)) next[p.id] = true; });
    setSelected(next);
    toast({ title: `${Object.keys(next).length} prospect(s) ciblé(s)`, description: `Ville : ${form.ville}` });
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await generateCampaign({ region: form.region, ville: form.ville, secteur: form.secteur, kind: form.kind });
      setTemplate(res.data);
      if (!form.nom) setForm((f) => ({ ...f, nom: res.data.subject?.slice(0, 60) || "Campagne" }));
      toast({ title: "Contenu généré ✓", description: "Aperçu disponible ci-dessous." });
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Génération IA impossible", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) return toast({ title: "Sélectionnez des prospects", variant: "destructive" });
    const ok = await onCreate({
      nom: form.nom, kind: form.kind, prospect_ids: selectedIds, region: form.region, ville: form.ville, secteur: form.secteur,
      subject: template?.subject || "", html_body: template?.html_body || "", image_url: template?.image_url || "", scheduled_at: form.scheduled_at || "",
    });
    if (ok) { setForm(emptyForm); setSelected({}); setTemplate(null); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-xl p-5 mb-6" data-testid="campaign-form">
      <div className="grid sm:grid-cols-4 gap-3 mb-4">
        <Field label="Région"><input className={inputCls} placeholder="Hauts-de-France" value={form.region} onChange={set("region")} /></Field>
        <Field label="Ville"><input className={inputCls} placeholder="Lens, Arras..." value={form.ville} onChange={set("ville")} /></Field>
        <Field label="Secteur"><input className={inputCls} placeholder="BTP, tertiaire..." value={form.secteur} onChange={set("secteur")} /></Field>
        <Field label="Type">
          <select className={inputCls} value={form.kind} onChange={set("kind")}>
            <option value="prospection">Prospection</option>
            <option value="relance">Relance</option>
          </select>
        </Field>
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <button type="button" onClick={generate} disabled={generating} data-testid="campaign-generate-btn" className="flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-semibold px-4 py-2.5 rounded-md disabled:opacity-60">
          {generating ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
          {generating ? "Génération IA..." : "Générer le contenu IA (avec image)"}
        </button>
        <button type="button" onClick={selectByVille} className="flex items-center gap-2 border border-neutral-300 text-neutral-700 hover:border-brand text-sm font-semibold px-4 py-2.5 rounded-md">Cibler par ville</button>
      </div>
      {template && (
        <div className="mb-4 border border-brand/30 bg-brand/5 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-brand/20 flex items-center gap-2">
            <Sparkles size={15} className="text-brand" />
            <span className="text-sm font-semibold text-neutral-800">Objet : {template.subject}</span>
          </div>
          <div className="max-h-72 overflow-y-auto bg-white" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.html_body || "") }} />
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <Field label="Nom de la campagne"><input required className={inputCls} placeholder="Ex: Prospection BTP Lens" value={form.nom} onChange={set("nom")} /></Field>
        <Field label="Programmer l'envoi (optionnel)"><input type="datetime-local" className={inputCls} value={form.scheduled_at} onChange={set("scheduled_at")} /></Field>
      </div>
      <label className="block text-xs font-semibold text-neutral-600 mb-2">Destinataires ({selectedIds.length} sélectionnés)</label>
      <RecipientPicker prospects={prospects} selected={selected} toggle={toggle} />
      <button type="submit" data-testid="campaign-submit-btn" className="mt-4 w-full bg-brand hover:bg-brand-dark text-white font-semibold py-2.5 rounded-md">
        {form.scheduled_at ? "Programmer la campagne" : "Créer la campagne"}
      </button>
    </form>
  );
};

export default CampaignForm;
